<div class="page-container">
  <app-page-header
    title="Create Commercial"
    [showBackButton]="true"
    [backRoute]="requestId ? '/production-requests/' + requestId : '/production-requests'"
    backLabel="Back to request">
  </app-page-header>

  <div class="content-wrapper">
    @if (!allowCreate) {
      <div class="blocked-message" role="alert">
        <mat-icon>info</mat-icon>
        <p>Cannot create a new commercial while this request is waiting for user approval. The user must decline first to allow a new version.</p>
        <a mat-stroked-button [routerLink]="['/production-requests', requestId]">Back to request</a>
      </div>
    } @else {
    <p class="muted">Upload an audio file or record a new commercial for this request.</p>

    <mat-tab-group class="create-commercial-tabs">
      <mat-tab label="Upload">
        <section class="panel">
          <div class="talent">
            <h4>Voice Actor</h4>
            @if (!selectedTalent) {
            <mat-form-field appearance="outline" class="talent-search">
              <mat-label>Search or add voice actor</mat-label>
              <input matInput [(ngModel)]="talentInput" (input)="onTalentInputChange(talentInput)"
                [matAutocomplete]="talentAuto" placeholder="Type to search or create new talent" />
              <mat-autocomplete #talentAuto="matAutocomplete" [displayWith]="displayTalentName"
                (optionSelected)="onTalentSelectionChange($event.option.value)">
                @for (t of (filteredTalents$ | async); track t.id) {
                <mat-option [value]="t">{{ t.name }}</mat-option>
                }
              </mat-autocomplete>
              <mat-icon matSuffix>search</mat-icon>
            </mat-form-field>
            }

            @if (selectedTalent) {
            <div class="selected-talent">
              <span class="selected-label"><mat-icon>check_circle</mat-icon> Selected voice actor</span>
              <div class="talent-chip">
                <mat-icon>person</mat-icon>
                <span>{{ selectedTalent.name }}</span>
                <button type="button" class="chip-remove" (click)="removeSelectedTalent()"
                  aria-label="Remove voice actor">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>
            } @else if (talentInput.trim() && !isTalentExisting()) {
            <button mat-stroked-button color="accent" type="button" (click)="createTalent()">
              <mat-icon>person_add</mat-icon> Add "{{ talentInput }}"
            </button>
            }
          </div>

          @if (!uploadFile || !uploadFileUrl) {
            <div class="uploader" (drop)="onDrop($event)" (dragover)="onDragOver($event)" tabindex="0" role="button">
              <mat-icon>cloud_upload</mat-icon>
              <p>Drag and drop your audio here, or</p>
              <button mat-stroked-button type="button" (click)="uploadFileInput.click()">Choose File</button>
              <input #uploadFileInput type="file" [accept]="accept" (change)="onUploadFileChange($event)" hidden />
            </div>
          } @else {
            <div class="preview-card">
              <div class="preview-audio-block">
                <div class="preview-header">
                  <h3>Audio preview</h3>
                  <button mat-stroked-button type="button" class="change-file-btn" (click)="uploadFileInput.click()">
                    Change file
                  </button>
                  <input #uploadFileInput type="file" [accept]="accept" (change)="onUploadFileChange($event)" hidden />
                </div>
                <audio [src]="uploadFileUrl" controls preload="metadata"></audio>
              </div>

              <div class="product-type-section">
                <p class="product-type-hint">Assign at least one product type before submitting this commercial.</p>
                <div class="product-type-field" [class.has-error]="productTypeError">
                  <app-product-type-selector
                    label="Assign Product Types *"
                    [panelMode]="true"
                    [showInlineLoadingBar]="false"
                    (selectionChange)="onProductTypeSelectionChange($event)">
                  </app-product-type-selector>
                  @if (productTypeError) {
                    <p class="field-error" role="alert">{{ productTypeError }}</p>
                  }
                </div>
              </div>

              <div class="preview-actions">
                <button mat-stroked-button type="button" (click)="clearUpload()">Remove</button>
                <button
                  mat-raised-button
                  color="primary"
                  type="button"
                  class="submit-commercial-btn"
                  (click)="submitUpload()"
                  [disabled]="submitting || !selectedTalent">
                  <span class="submit-commercial-btn__content">
                    @if (submitting) {
                      <mat-spinner diameter="20" class="submit-commercial-spinner" />
                      <span>Uploading…</span>
                    } @else {
                      <span>Submit</span>
                    }
                  </span>
                </button>
              </div>
            </div>
          }
          @if (uploadFileError) {
            <p class="error" role="alert">{{ uploadFileError }}</p>
          }
        </section>
      </mat-tab>

      <mat-tab label="Record">
        <section class="panel">
          <div class="talent">
            <h4>Voice Actor</h4>
            @if (!selectedTalent) {
            <mat-form-field appearance="outline" class="talent-search">
              <mat-label>Search or add voice actor</mat-label>
              <input matInput [(ngModel)]="talentInput" (input)="onTalentInputChange(talentInput)"
                [matAutocomplete]="recordTalentAuto" placeholder="Type to search or create new talent" />
              <mat-autocomplete #recordTalentAuto="matAutocomplete" [displayWith]="displayTalentName"
                (optionSelected)="onTalentSelectionChange($event.option.value)">
                @for (t of (filteredTalents$ | async); track t.id) {
                <mat-option [value]="t">{{ t.name }}</mat-option>
                }
              </mat-autocomplete>
              <mat-icon matSuffix>search</mat-icon>
            </mat-form-field>
            }

            @if (selectedTalent) {
            <div class="selected-talent">
              <span class="selected-label"><mat-icon>check_circle</mat-icon> Selected voice actor</span>
              <div class="talent-chip">
                <mat-icon>person</mat-icon>
                <span>{{ selectedTalent.name }}</span>
                <button type="button" class="chip-remove" (click)="removeSelectedTalent()"
                  aria-label="Remove voice actor">
                  <mat-icon>close</mat-icon>
                </button>
              </div>
            </div>
            } @else if (talentInput.trim() && !isTalentExisting()) {
            <button mat-stroked-button color="accent" type="button" (click)="createTalent()">
              <mat-icon>person_add</mat-icon> Add "{{ talentInput }}"
            </button>
            }
          </div>

          <div class="record-controls">
            @if (isRecording) {
              <button mat-raised-button color="warn" (click)="stopRecording()">
                <mat-icon>stop</mat-icon> Stop
              </button>
              <span class="timer" aria-live="polite">{{ timerDisplay }}</span>
            } @else if (showRecordingPlayer && recordingUrl) {
              <div class="preview-card">
                <div class="preview-audio-block">
                  <h3>Recording preview</h3>
                  <audio [src]="recordingUrl" controls preload="metadata"></audio>
                </div>

                <div class="product-type-section">
                  <p class="product-type-hint">Assign at least one product type before submitting this commercial.</p>
                  <div class="product-type-field" [class.has-error]="productTypeError">
                    <app-product-type-selector
                      label="Assign Product Types *"
                      [panelMode]="true"
                      [showInlineLoadingBar]="false"
                      (selectionChange)="onProductTypeSelectionChange($event)">
                    </app-product-type-selector>
                    @if (productTypeError) {
                      <p class="field-error" role="alert">{{ productTypeError }}</p>
                    }
                  </div>
                </div>

                <div class="preview-actions">
                  <button mat-stroked-button type="button" (click)="clearRecording()">Discard</button>
                  <button
                    mat-raised-button
                    color="primary"
                    type="button"
                    class="submit-commercial-btn"
                    (click)="submitRecording()"
                    [disabled]="submitting || !selectedTalent">
                    <span class="submit-commercial-btn__content">
                      @if (submitting) {
                        <mat-spinner diameter="20" class="submit-commercial-spinner" />
                        <span>Uploading…</span>
                      } @else {
                        <span>Submit recording</span>
                      }
                    </span>
                  </button>
                </div>
              </div>
            } @else {
              <button mat-raised-button color="primary" (click)="startRecording()">
                <mat-icon>fiber_manual_record</mat-icon> Start Recording
              </button>
            }
          </div>
          @if (uploadFileError) {
            <p class="error" role="alert">{{ uploadFileError }}</p>
          }
        </section>
      </mat-tab>
    </mat-tab-group>

    @if (submitting) {
      <mat-progress-bar mode="indeterminate" class="top-progress"></mat-progress-bar>
    }
    }
  </div>
</div>
